/**
 * The remote-command form, per OCPP version.
 *
 * Keyed by command name within a version, because the names collide across
 * versions with different shapes: 1.6's `Reset` is `Soft | Hard`, 2.0.1's is
 * `Immediate | OnIdle`; 1.6 unlocks a connector, 2.0.1 a connector on an EVSE.
 * Only the fields an operator sets are listed — optional protocol fields that
 * nobody sends by hand are deliberately omitted rather than cluttering the form.
 *
 * A field `name` may be a dotted path (`idToken.type`), which the payload
 * nests. A command whose wire shape is a batch (`SetVariables`) declares a
 * `shape` that wraps the form's one entry.
 */

export interface CommandField {
  readonly name: string;
  readonly label: string;
  /** `list` collects a comma-separated set and sends a JSON array. */
  readonly type: "text" | "number" | "enum" | "list";
  readonly options?: readonly string[];
  readonly required?: boolean;
  readonly hint?: string;
  readonly defaultValue?: string;
}

export interface CommandForm {
  readonly fields: readonly CommandField[];
  /** Turns the flat form payload into the command's wire shape. */
  readonly shape?: (payload: Record<string, unknown>) => Record<string, unknown>;
}

const FIELDS_V16: Readonly<Record<string, readonly CommandField[]>> = {
  Reset: [
    {
      name: "type",
      label: "Reset type",
      type: "enum",
      options: ["Soft", "Hard"],
      required: true,
      defaultValue: "Soft",
      hint: "Soft restarts the application; Hard power-cycles the controller.",
    },
  ],
  UnlockConnector: [
    { name: "connectorId", label: "Connector", type: "number", required: true, defaultValue: "1" },
  ],
  RemoteStartTransaction: [
    {
      name: "idTag",
      label: "ID tag",
      type: "text",
      required: true,
      hint: "The card this session is billed to. Sent verbatim.",
    },
    { name: "connectorId", label: "Connector", type: "number" },
  ],
  RemoteStopTransaction: [
    {
      name: "transactionId",
      label: "Transaction id",
      type: "number",
      required: true,
      hint: "From the station's StartTransaction response, visible in the frames below.",
    },
  ],
  ChangeAvailability: [
    { name: "connectorId", label: "Connector", type: "number", required: true, defaultValue: "0" },
    {
      name: "type",
      label: "Availability",
      type: "enum",
      options: ["Operative", "Inoperative"],
      required: true,
      defaultValue: "Inoperative",
      hint: "Connector 0 applies to the whole station.",
    },
  ],
  ChangeConfiguration: [
    { name: "key", label: "Key", type: "text", required: true },
    { name: "value", label: "Value", type: "text", required: true },
  ],
  GetConfiguration: [
    {
      name: "key",
      label: "Keys (optional)",
      // An array on the wire even for a single key — the schema takes
      // `string[]`, and sending a bare string would be a 400.
      type: "list",
      hint: "Comma separated. Leave empty to read the station's whole configuration.",
    },
  ],
  SetMeterValuesSampledData: [
    {
      name: "measurands",
      label: "Sampled measurands",
      type: "list",
      required: true,
      defaultValue: "Energy.Active.Import.Register",
      hint: "Comma separated. Validated against the OCPP measurand enum server-side, then sent as a ChangeConfiguration for MeterValuesSampledData.",
    },
  ],
  TriggerMessage: [
    {
      name: "requestedMessage",
      label: "Message",
      type: "enum",
      // Exactly the schema's enum. A member listed here but absent there would be
      // a dropdown option that always 400s.
      options: [
        "StatusNotification",
        "BootNotification",
        "Heartbeat",
        "MeterValues",
        "DiagnosticsStatusNotification",
        "FirmwareStatusNotification",
      ],
      required: true,
      defaultValue: "StatusNotification",
    },
    { name: "connectorId", label: "Connector (optional)", type: "number" },
  ],
};

const ID_TOKEN_TYPES = [
  "Central",
  "ISO14443",
  "ISO15693",
  "KeyCode",
  "Local",
  "MacAddress",
  "eMAID",
  "NoAuthorization",
] as const;

const FIELDS_V201: Readonly<Record<string, readonly CommandField[]>> = {
  Reset: [
    {
      name: "type",
      label: "Reset type",
      type: "enum",
      options: ["OnIdle", "Immediate"],
      required: true,
      defaultValue: "OnIdle",
      hint: "OnIdle waits for running transactions to end; Immediate does not.",
    },
    { name: "evseId", label: "EVSE (optional)", type: "number", hint: "Empty resets the station." },
  ],
  UnlockConnector: [
    { name: "evseId", label: "EVSE", type: "number", required: true, defaultValue: "1" },
    { name: "connectorId", label: "Connector", type: "number", required: true, defaultValue: "1" },
  ],
  RequestStartTransaction: [
    {
      name: "idToken.idToken",
      label: "ID token",
      type: "text",
      required: true,
      hint: "The token this session is billed to. Sent verbatim.",
    },
    {
      name: "idToken.type",
      label: "Token type",
      type: "enum",
      options: ID_TOKEN_TYPES,
      required: true,
      defaultValue: "Central",
    },
    { name: "evseId", label: "EVSE", type: "number" },
  ],
  RequestStopTransaction: [
    {
      name: "transactionId",
      label: "Transaction id",
      type: "text",
      required: true,
      hint: "The station's own id, from its TransactionEvent, visible in the frames below.",
    },
  ],
  ChangeAvailability: [
    {
      name: "operationalStatus",
      label: "Availability",
      type: "enum",
      options: ["Operative", "Inoperative"],
      required: true,
      defaultValue: "Inoperative",
    },
    {
      name: "evse.id",
      label: "EVSE (optional)",
      type: "number",
      hint: "Empty applies to the whole station.",
    },
  ],
  SetVariables: [
    { name: "component", label: "Component", type: "text", required: true },
    { name: "variable", label: "Variable", type: "text", required: true },
    { name: "attributeValue", label: "Value", type: "text", required: true },
  ],
  GetVariables: [
    { name: "component", label: "Component", type: "text", required: true },
    { name: "variable", label: "Variable", type: "text", required: true },
  ],
  SetMeterValuesSampledData: [
    {
      name: "measurands",
      label: "Sampled measurands",
      type: "list",
      required: true,
      defaultValue: "Energy.Active.Import.Register",
      hint: "Comma separated. Validated against the OCPP measurand enum server-side, then sent as a SetVariables of SampledDataCtrlr.TxUpdatedMeasurands.",
    },
  ],
  TriggerMessage: [
    {
      name: "requestedMessage",
      label: "Message",
      type: "enum",
      // Exactly the server schema's enum, as for 1.6.
      options: [
        "StatusNotification",
        "BootNotification",
        "Heartbeat",
        "MeterValues",
        "TransactionEvent",
        "FirmwareStatusNotification",
        "LogStatusNotification",
      ],
      required: true,
      defaultValue: "StatusNotification",
    },
    { name: "evse.id", label: "EVSE (optional)", type: "number" },
  ],
};

/** One variable, as the batch 2.0.1 sends. */
function oneVariable(key: "setVariableData" | "getVariableData") {
  return ({ component, variable, ...rest }: Record<string, unknown>) => ({
    [key]: [{ ...rest, component: { name: component }, variable: { name: variable } }],
  });
}

const SHAPES_V201: Readonly<Record<string, CommandForm["shape"]>> = {
  SetVariables: oneVariable("setVariableData"),
  GetVariables: oneVariable("getVariableData"),
};

/** The form for one command on a station of this version. */
export function commandForm(version: string | null, command: string): CommandForm {
  if (version === "ocpp2.0.1") {
    const shape = SHAPES_V201[command];
    return { fields: FIELDS_V201[command] ?? [], ...(shape === undefined ? {} : { shape }) };
  }
  return { fields: FIELDS_V16[command] ?? [] };
}

/**
 * Empty optional fields are omitted from the payload rather than sent as `""` or
 * `0`. OCPP validates types strictly, and `{"connectorId": 0}` is a meaningful
 * value (the whole station) that must never be produced by an untouched input.
 */
export function buildPayload(
  form: CommandForm,
  values: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const fields = form.fields;
  let payload: Record<string, unknown> = {};

  for (const field of fields) {
    const raw = (values[field.name] ?? "").trim();
    if (raw === "") continue;

    if (field.type === "number") {
      const parsed = Number(raw);
      // Not silently coerced: a non-numeric string in a numeric field is dropped
      // rather than sent as NaN, and the required-field check has already stopped
      // the send if it mattered.
      if (!Number.isFinite(parsed)) continue;
      payload = withPath(payload, field.name, parsed);
      continue;
    }

    if (field.type === "list") {
      const items = raw
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
      // An all-whitespace list is an absent list, not an empty array — the schema
      // requires `nonempty()` and would reject `[]` with a confusing message.
      if (items.length === 0) continue;
      payload = withPath(payload, field.name, items);
      continue;
    }

    payload = withPath(payload, field.name, raw);
  }

  return form.shape === undefined ? payload : form.shape(payload);
}

/** A copy of `target` with `value` at a dotted path. */
function withPath(
  target: Readonly<Record<string, unknown>>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const [head = "", ...rest] = path.split(".");
  if (rest.length === 0) return { ...target, [head]: value };

  const child = target[head];
  return {
    ...target,
    [head]: withPath(
      typeof child === "object" && child !== null ? (child as Record<string, unknown>) : {},
      rest.join("."),
      value,
    ),
  };
}
